export type ColorScheme = 'light' | 'dark';

export const DEFAULT_COLOR_SCHEME: ColorScheme = 'light';
export const COLOR_SCHEME_COOKIE_KEY = 'prana-color-scheme';
export const COLOR_SCHEME_STORAGE_KEY = 'prana-color-scheme';

export const PLATFORM_SIDEBAR_COLLAPSED_COOKIE_KEY = 'prana-platform-sidebar-collapsed';
export const PLATFORM_SIDEBAR_COLLAPSED_STORAGE_KEY = 'prana-platform-sidebar-collapsed';

export const normalizeColorScheme = (value?: string | null): ColorScheme => {
  return value === 'dark' ? 'dark' : DEFAULT_COLOR_SCHEME;
};

export const normalizePlatformSidebarCollapsed = (value?: string | null): boolean => {
  return value === '1' || value === 'true';
};

const UI_PREFERENCES_READY_EVENT = 'prana-ui-preferences-ready';

export const readUiPreferenceCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;

  try {
    const prefix = `${name}=`;
    const parts = (document.cookie || '').split(';');

    for (const rawPart of parts) {
      const part = rawPart.trim();

      if (part.startsWith(prefix)) {
        return decodeURIComponent(part.slice(prefix.length));
      }
    }
  } catch {
    return null;
  }

  return null;
};

export const readUiPreferenceStorage = (name: string): string | null => {
  if (typeof window === 'undefined') return null;

  try {
    return window.localStorage.getItem(name);
  } catch {
    return null;
  }
};

export const getStoredColorScheme = (): ColorScheme => {
  const storedTheme = readUiPreferenceStorage(COLOR_SCHEME_STORAGE_KEY);

  if (storedTheme === 'dark' || storedTheme === 'light') {
    return storedTheme;
  }

  return normalizeColorScheme(readUiPreferenceCookie(COLOR_SCHEME_COOKIE_KEY));
};

export const getStoredPlatformSidebarCollapsed = (): boolean => {
  const storedCollapsed = readUiPreferenceStorage(PLATFORM_SIDEBAR_COLLAPSED_STORAGE_KEY);
  const isStoredValueValid =
    storedCollapsed === '1' ||
    storedCollapsed === '0' ||
    storedCollapsed === 'true' ||
    storedCollapsed === 'false';

  return normalizePlatformSidebarCollapsed(
    isStoredValueValid
      ? storedCollapsed
      : readUiPreferenceCookie(PLATFORM_SIDEBAR_COLLAPSED_COOKIE_KEY),
  );
};

export const applyStoredUiPreferences = (): void => {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  const theme = getStoredColorScheme();
  const isSidebarCollapsed = getStoredPlatformSidebarCollapsed();

  root.dataset.theme = theme;
  root.style.colorScheme = theme;
  root.dataset.platformSidebarCollapsed = isSidebarCollapsed ? 'true' : 'false';
  root.dataset.uiPreferencesReady = 'true';
  document.dispatchEvent(new Event(UI_PREFERENCES_READY_EVENT));
};

export { UI_PREFERENCES_READY_EVENT };
